import { z } from "zod";

import {
  BatchSchema,
  ClusterSchema,
  FileSchema,
  CompanyMachineSchema,
  JobSchema,
  LayoutSchema,
  MachineSchema,
  MaterialLayoutSchema,
  NestingRunSchema,
  ProcessStepSchema,
  ProductLineSchema,
  ProductSchema,
  QuoteRequestLineSchema,
} from "../generated/prisma/zod";
import { ExtendedPartSchema } from "../types";

export const FindOneQuoteRequestLineProcessStepsSchema = ProcessStepSchema.merge(
  z.object({
    materialLayout: MaterialLayoutSchema.nullable(),
    companyMachine: CompanyMachineSchema.merge(
      z.object({
        machine: MachineSchema,
      }),
    ).nullable(),
    productLines: ProductLineSchema.merge(
      z.object({
        product: ProductSchema,
      }),
    ).array(),
  }),
).array();

const findOneQuoteRequestLineOutputSchema = QuoteRequestLineSchema.merge(
  z.object({
    processSteps: FindOneQuoteRequestLineProcessStepsSchema,
    cluster: ClusterSchema.merge(
      z.object({
        targetQuoteRequestLines: QuoteRequestLineSchema.merge(
          z.object({
            part: ExtendedPartSchema.merge(
              z.object({
                files: FileSchema.array().nullable(),
              }),
            ),
            layout: LayoutSchema.merge(
              z.object({
                materialLayout: MaterialLayoutSchema.nullable(),
              }),
            ).nullable(),
            processSteps: ProcessStepSchema.array(),
          }),
        ).array(),
        excludedProcessSteps: ProcessStepSchema.array(),
      }),
    ).nullable(),
    part: ExtendedPartSchema.merge(
      z.object({
        files: FileSchema.array(),
      }),
    ),
    layout: LayoutSchema.merge(
      z.object({
        materialLayout: MaterialLayoutSchema.nullable(),
      }),
    ).nullable(),
    batch: BatchSchema.nullable(),
    productLines: ProductLineSchema.merge(
      z.object({
        product: ProductSchema,
      }),
    ).array(),
    cosineSimilarity: z.number().optional(),
  }),
);

export type FindOneQuoteRequestLineOutput = z.infer<typeof findOneQuoteRequestLineOutputSchema>;
export const FindOneQuoteRequestLineOutputSchema: z.ZodType<FindOneQuoteRequestLineOutput> =
  findOneQuoteRequestLineOutputSchema;

const findOneQuoteRequestLineWithoutCosineSchema = findOneQuoteRequestLineOutputSchema.omit({
  cosineSimilarity: true,
});

export type FindOneQuoteRequestLineWithoutCosine = z.infer<
  typeof findOneQuoteRequestLineWithoutCosineSchema
>;
export const FindOneQuoteRequestLineWithoutCosineSchema: z.ZodType<FindOneQuoteRequestLineWithoutCosine> =
  findOneQuoteRequestLineWithoutCosineSchema;

const findOneQuoteRequestLineWithLatestJobSchema =
  findOneQuoteRequestLineWithoutCosineSchema.extend({
    latestPredictionJob: JobSchema.nullable(),
    multiPartNestingOptimizationId: z.string().nullable().optional(),
    nestingStatus: NestingRunSchema.shape.status.nullable().optional(),
  });

export type FindOneQuoteRequestLineWithLatestJob = z.infer<
  typeof findOneQuoteRequestLineWithLatestJobSchema
>;
export const FindOneQuoteRequestLineWithLatestJobSchema: z.ZodType<FindOneQuoteRequestLineWithLatestJob> =
  findOneQuoteRequestLineWithLatestJobSchema;
